import { Input, Label } from '@teach/ui'
import type { CourseWithoutBlocks, CourseUpdate } from '@/types/course'
import { clearable } from '../../lib/coursePatch'
import { REVIEW_LOCK_TEXT } from '../../lib/reviewLockText'
import { FieldHint } from '../shared/FieldHint'
import { StyleReferencesGallery } from './StyleReferencesGallery'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  local: CourseWithoutBlocks
  setLocal: (next: CourseWithoutBlocks) => void
  save: (patch: CourseUpdate) => Promise<boolean>
  isCourseAuthor: boolean
  /** Курс на проверке: стиль и образцы уходят в ту же заявку, поэтому гаснут. */
  isFrozen: boolean
}

/** Вкладка «Дизайн-система»: единый стиль и образцы для всех картинок курса. */
export const DesignSystemSection = ({ local, setLocal, save, isCourseAuthor, isFrozen }: Props) => {
  const m = useMessages(messages)
  const disabledTitle = !isCourseAuthor ? m.noRights : undefined
  const assetDisabled = !isCourseAuthor || isFrozen
  const assetTitle = isFrozen && isCourseAuthor ? REVIEW_LOCK_TEXT : disabledTitle

  return (
    <div className="space-y-5 [&_label]:text-lg [&_label]:font-medium">
      <div className="space-y-3">
        <div className="flex items-center gap-1.5">
          <Label htmlFor="course-image-style">{m.imageStyle}</Label>
          <FieldHint label={m.imageStyleHintLabel}>{m.imageStyleHint}</FieldHint>
        </div>
        <Input
          id="course-image-style"
          value={local.imageStyle ?? ''}
          onChange={(e) => setLocal({ ...local, imageStyle: e.target.value })}
          // `trim()` не косметика: гейт запуска отрисовки считает стиль
          // непустым по `trim()`, а сервер — по длине строки. Сохранив «   »,
          // мы получили бы непустое поле на сервере и вечно погашенную кнопку
          // с текстом «стиль не задан» над непустым на вид полем (M-9).
          onBlur={() => void save({ imageStyle: clearable(local.imageStyle).trim() })}
          disabled={assetDisabled}
          title={assetTitle}
          placeholder={m.imageStylePlaceholder}
          className="rounded-xl"
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-1.5">
          <Label>{m.styleRefs}</Label>
          <FieldHint label={m.styleRefsHintLabel}>{m.styleRefsHint}</FieldHint>
        </div>
        <StyleReferencesGallery
          value={local.styleReferences ?? []}
          disabled={assetDisabled}
          disabledHint={assetTitle}
          onChange={(next) => {
            setLocal({ ...local, styleReferences: next })
            return save({ styleReferences: next })
          }}
        />
      </div>
    </div>
  )
}
